import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router";
import { StatusCode } from "api/protocols";
import "./TournamentHome.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck, faMinus, faPlus } from "@fortawesome/pro-solid-svg-icons";
import { faCircle } from "@fortawesome/pro-regular-svg-icons";
import classNames from "classnames";
import { IUIActions } from "redux/actions/ui";
import { ICartState } from "redux/reducers/customer/cart";
import { ICartActions } from "redux/actions/customer/cart";
import { ICart } from "redux/reducers/customer/models/cart";
import OrderSummary from "components/OrderSummary/OrderSummary";
import { ITournament, ITournamentRegistrationFee } from "redux/reducers/models/tournament";
import { IFacility } from "pages/guest/models/facility";
import TournamentHeader from "./TournamentHeader";
import { GetTournamentRegistrationFee } from "api/rpc/2024-04/guest/tournament/registration";
import { useTranslation } from "react-i18next";
import Page from "components/page/Page";
import Callout from "components/callout/Callout";
import { ButtonNew as Button } from "components/buttonNew";
import { ITournamentRegistrationActions } from "redux/actions/customer/tournamentRegistration";
import {
  ISelectedTournamentRegistrationFee,
  ITournamentRegistrationState,
} from "redux/reducers/customer/tournamentRegistration";
import { cloneDeep } from "lodash";
import { useWindowSize } from "hooks/useWindowSize/useWindowSize";
import { TABLET_WIDTH } from "helpers/ScreenSizes";
import Card from "components/card/Card";
import Markdown from "components/markdown/Markdown";

interface ITournamentHomeParams {
  facilityShortName: string;
  tournamentHandle: string;
}

interface ITournamentHomeProps {
  cartStore: ICartState;
  cartActions: ICartActions;
  uiActions: IUIActions;
  tournamentRegistrationStore: ITournamentRegistrationState;
  tournamentRegistrationActions: ITournamentRegistrationActions;
}

interface ITournamentHomeState {
  fullDescriptionVisible: boolean;
  descriptionOverflow: boolean;
  hoveredTournamentRegistrationFeeId: number;
  selectedTournamentRegistrationFees: ISelectedTournamentRegistrationFee[];
  tournamentRegistrationFees: ITournamentRegistrationFee[];
}

export function getTournamentCartTokenName(facilityShortName: string, tournamentHandle: string) {
  return `${facilityShortName}_${tournamentHandle}_tournament_registration_cart_token`;
}

export function getTournamentCartToken(facilityShortName: string, tournamentHandle: string) {
  return localStorage.getItem(getTournamentCartTokenName(facilityShortName, tournamentHandle));
}

type TournamentRegistrationWarning = "closed" | "will-open" | "closed-previously" | "no-capacity" | "none";

type TournamentRegistrationAmountWarning = "too-few" | "too-many" | "none";

export function determineTournamentRegistrationWarning(tournament: ITournament): TournamentRegistrationWarning {
  if (!tournament?.registration_enabled || tournament?.display_registration_online === false) {
    return "closed";
  }

  const currentDate = new Date();
  const openDate = !tournament.registration_open_date ? null : new Date(tournament.registration_open_date + "Z");
  const closeDate = !tournament.registration_close_date ? null : new Date(tournament.registration_close_date + "Z");

  if (openDate && openDate.getTime() > currentDate.getTime()) {
    return "will-open";
  } else if (closeDate && closeDate.getTime() <= currentDate.getTime()) {
    return "closed-previously";
  } else if (tournament.capacity_remaining <= 0) {
    return "no-capacity";
  }

  return "none";
}

export default function TournamentHome(props: ITournamentHomeProps) {
  const { facilityShortName, tournamentHandle } = useParams<ITournamentHomeParams>();
  const history = useHistory();
  const { t } = useTranslation();
  const { cartStore, cartActions, uiActions, tournamentRegistrationStore, tournamentRegistrationActions } = props;

  const windowSize = useWindowSize();

  const [tournamentHomeState, setTournamentHomeState] = useState<ITournamentHomeState>({
    fullDescriptionVisible: false,
    descriptionOverflow: false,
    hoveredTournamentRegistrationFeeId: undefined,
    selectedTournamentRegistrationFees: [],
    tournamentRegistrationFees: [],
  });

  useEffect(() => {
    void loadTournamentHome();
  }, []);

  useEffect(() => {
    if (
      tournamentRegistrationStore.tournament &&
      determineTournamentRegistrationWarning(tournamentRegistrationStore.tournament) !== "none"
    ) {
      setTournamentHomeState(prevState => ({ ...prevState, fullDescriptionVisible: true }));
    }
    void checkDescriptionOverflow();
  }, [tournamentRegistrationStore.tournament]);

  async function loadTournamentHome() {
    let facility: IFacility = undefined;

    uiActions.enqueue();

    try {
      const getFacilityPromise: Promise<IFacility> = tournamentRegistrationStore.facility
        ? Promise.resolve(tournamentRegistrationStore.facility)
        : (tournamentRegistrationActions.loadFacility(facilityShortName, true) as unknown as Promise<IFacility>);

      facility = await getFacilityPromise;
    } catch {
      uiActions.showError(t("Failed to load facility"));
      uiActions.dequeue();
      return;
    }

    const facilityId = facility.id;

    let tournament: ITournament = undefined;

    try {
      const getTournamentPromise: Promise<ITournament> = tournamentRegistrationStore.tournament
        ? Promise.resolve(tournamentRegistrationStore.tournament)
        : (tournamentRegistrationActions.loadTournament(
            facilityId,
            tournamentHandle,
            true,
          ) as unknown as Promise<ITournament>);

      tournament = await getTournamentPromise;
    } catch {
      uiActions.showError(t("Failed to load tournament"));
      uiActions.dequeue();
      return;
    }

    if (!tournament) {
      uiActions.showError(t("Tournament could not be found"));
      uiActions.dequeue();
      return;
    }

    const getTournamentRegistrationFeeResponse = await GetTournamentRegistrationFee(
      { facility_id: facilityId, tournament_id: tournament.id },
      true,
    );

    if (getTournamentRegistrationFeeResponse.status !== StatusCode.OK) {
      uiActions.showError(t("Failed to load tournament registration fees"));
      uiActions.dequeue();
      return;
    }

    setTournamentHomeState(prev => ({
      ...prev,
      tournamentRegistrationFees: getTournamentRegistrationFeeResponse.data,
    }));

    let cart: ICart = undefined;

    if (getTournamentCartToken(facilityShortName, tournamentHandle)) {
      try {
        const getCartPromise: Promise<ICart> = cartStore?.isLoaded
          ? Promise.resolve(cartStore?.cart)
          : (cartActions.loadCart(
              {
                facility_id: facilityId,
                tokenName: getTournamentCartTokenName(facilityShortName, tournamentHandle),
                token: getTournamentCartToken(facilityShortName, tournamentHandle),
              },
              true,
            ) as unknown as Promise<ICart>);

        cart = await getCartPromise;
      } catch {
        uiActions.showError(t("Failed to load cart"));
        uiActions.dequeue();
        return;
      }
    }

    if (cart?.status === "complete") {
      cartActions.cartClear({ tokenName: getTournamentCartTokenName(facilityShortName, tournamentHandle) });
    }

    uiActions.dequeue();
  }

  function calculateTournamentSpotsLeft(
    capacityRemaining: number,
    registrationFees: ISelectedTournamentRegistrationFee[],
  ): number {
    return registrationFees
      .filter(registrationFee => registrationFee.type === "registration_fee")
      .map(registrationFee => registrationFee.players * registrationFee.quantity)
      .reduce((prev, next) => prev - next, capacityRemaining);
  }

  function getTournamentSpotsLeft() {
    if (tournamentRegistrationStore?.tournament?.capacity_remaining != null) {
      if (tournamentHomeState.selectedTournamentRegistrationFees?.length > 0) {
        return calculateTournamentSpotsLeft(
          tournamentRegistrationStore.tournament.capacity_remaining,
          tournamentHomeState.selectedTournamentRegistrationFees,
        );
      } else if (cartStore.cart?.line_items?.length > 0) {
        const activeRegistrationFees: ISelectedTournamentRegistrationFee[] = [];

        for (const lineItem of cartStore.cart.line_items) {
          const activeRegistrationFee = tournamentHomeState.tournamentRegistrationFees?.find(
            tournamentRegistrationFee => {
              return tournamentRegistrationFee.variant_id === lineItem.variant_id;
            },
          );

          if (activeRegistrationFee) {
            activeRegistrationFees.push({
              ...activeRegistrationFee,
              quantity: lineItem.quantity,
            });
          }
        }

        return calculateTournamentSpotsLeft(
          tournamentRegistrationStore.tournament.capacity_remaining,
          activeRegistrationFees,
        );
      } else {
        return tournamentRegistrationStore.tournament.capacity_remaining;
      }
    } else {
      return 0;
    }
  }

  const tournamentSpotsLeft = getTournamentSpotsLeft();

  function getTournamentRegistrationFeePrice(tournamentRegistrationFee: ITournamentRegistrationFee) {
    if (!tournamentRegistrationFee) {
      return undefined;
    }

    const price = tournamentRegistrationFee.price;

    const priceOverride = tournamentRegistrationFee.variant?.inventory_item?.inventory_levels?.find(
      inventoryLevel => inventoryLevel?.facility_id === tournamentRegistrationStore.facility?.id,
    )?.price_override;

    if (price) {
      return price;
    } else if (priceOverride) {
      return priceOverride;
    } else {
      return tournamentRegistrationFee.variant?.price;
    }
  }

  function formatTournamentRegistrationFeePrice(tournamentRegistrationFee: ITournamentRegistrationFee) {
    const price = getTournamentRegistrationFeePrice(tournamentRegistrationFee);

    if (price !== undefined) {
      return "$" + String(price);
    } else {
      return "";
    }
  }

  function getTotalRegistrationFeePrice(): number {
    let total = 0;
    for (let i = 0; i < tournamentHomeState.selectedTournamentRegistrationFees.length; i++) {
      total +=
        getTournamentRegistrationFeePrice(tournamentHomeState.selectedTournamentRegistrationFees[i]) *
        tournamentHomeState.selectedTournamentRegistrationFees[i].quantity;
    }
    return total;
  }

  function hoverTournamentRegistrationFee(id: number, isDisabled: boolean) {
    if (isDisabled) {
      return;
    }

    setTournamentHomeState(prev => ({
      ...prev,
      hoveredTournamentRegistrationFeeId: id,
    }));
  }

  function selectTournamentRegistrationFee(tournamentRegistrationFee: ITournamentRegistrationFee, isDisabled: boolean) {
    if (isDisabled) {
      return;
    }

    const temp = cloneDeep(tournamentHomeState.selectedTournamentRegistrationFees);

    const feeIndex = temp.findIndex(fee => fee.id === tournamentRegistrationFee.id);

    if (feeIndex >= 0) {
      temp.splice(feeIndex, 1);
    } else {
      temp.push({ ...tournamentRegistrationFee, quantity: 1 });
    }

    setTournamentHomeState(prev => ({
      ...prev,
      selectedTournamentRegistrationFees: temp,
    }));
  }

  function navigateToCheckout() {
    history.push(`/tournament/${facilityShortName}/${tournamentHandle}/checkout`);
  }

  function determineRegistrationAmountWarning(): TournamentRegistrationAmountWarning {
    const tournamentSpotsUsed =
      (tournamentRegistrationStore.tournament?.capacity_remaining ?? 0) - (tournamentSpotsLeft ?? 0);
    const minTournamentSpots = tournamentRegistrationStore.tournament?.minimum_registration_amount;
    const maxTournamentSpots = tournamentRegistrationStore.tournament?.maximum_registration_amount;

    if (minTournamentSpots != null && tournamentSpotsUsed < minTournamentSpots) {
      return "too-few";
    } else if (maxTournamentSpots != null && tournamentSpotsUsed > maxTournamentSpots) {
      return "too-many";
    } else {
      return "none";
    }
  }

  const tournamentRegistrationAmountWarning = determineRegistrationAmountWarning();

  function renderRegistrationAmountWarningCallout() {
    const minRegistrationAmount = tournamentRegistrationStore.tournament?.minimum_registration_amount;
    const maxRegistrationAmount = tournamentRegistrationStore.tournament?.maximum_registration_amount;

    switch (tournamentRegistrationAmountWarning) {
      case "too-few":
        return (
          <Callout
            type="warning"
            title={t("Too few players")}
            content={`${t("You must register at least")} ${minRegistrationAmount} ${
              minRegistrationAmount === 1 ? "player" : "players"
            }`}
          />
        );
      case "too-many":
        return (
          <Callout
            type="warning"
            title={t("Too many players")}
            content={`${t("You must register at most")} ${maxRegistrationAmount} ${
              maxRegistrationAmount === 1 ? "player" : "players"
            }`}
          />
        );
      default:
        return <></>;
    }
  }

  function disabledGoToCheckout() {
    return (
      ((!cartStore?.cart?.line_items || cartStore?.cart?.line_items?.length === 0) &&
        tournamentHomeState.selectedTournamentRegistrationFees.length <= 0) ||
      tournamentSpotsLeft < 0 ||
      tournamentRegistrationAmountWarning !== "none"
    );
  }

  async function goToCheckout() {
    if (disabledGoToCheckout()) {
      return;
    } else if (
      cartStore?.cart?.line_items?.length > 0 &&
      (!tournamentHomeState.selectedTournamentRegistrationFees ||
        tournamentHomeState.selectedTournamentRegistrationFees.length === 0)
    ) {
      const updatedActiveRegistrationFees: ISelectedTournamentRegistrationFee[] = [];

      for (const lineItem of cartStore.cart.line_items) {
        const activeRegistrationFee = tournamentHomeState.tournamentRegistrationFees?.find(
          tournamentRegistrationFee => {
            return tournamentRegistrationFee.variant_id === lineItem.variant_id;
          },
        );

        if (!activeRegistrationFee) {
          uiActions.showError(`${lineItem.product_title} is unavailable`);
          return;
        }

        updatedActiveRegistrationFees.push({
          ...activeRegistrationFee,
          quantity: lineItem.quantity,
        });
      }

      tournamentRegistrationActions.updateActiveRegistrationFees(updatedActiveRegistrationFees);
      navigateToCheckout();
      return;
    }

    uiActions.enqueue();

    let cart: ICart = undefined;

    if (cartStore.isLoaded) {
      cart = cartStore?.cart;
    } else {
      try {
        const postCartPromise: Promise<ICart> = cartActions.postCart(
          {
            tokenName: getTournamentCartTokenName(facilityShortName, tournamentHandle),
            facility_id: tournamentRegistrationStore.facility?.id,
          },
          false,
        ) as unknown as Promise<ICart>;

        cart = await postCartPromise;
      } catch {
        uiActions.dequeue();
        uiActions.showError(t("Failed to create cart"));
        return;
      }
    }

    if (cart?.line_items?.length > 0) {
      const lineItemCount = cart?.line_items?.length;
      for (let i = 0; i < lineItemCount; i++) {
        try {
          const deleteLineItemPromise: Promise<ICart> = cartActions.deleteLineItem(
            cart?.line_items?.[0].id,
            cart?.token,
            false,
          ) as unknown as Promise<ICart>;

          cart = await deleteLineItemPromise;
        } catch {
          uiActions.dequeue();
          uiActions.showError(t("Failed to delete existing line item"));
          return;
        }
      }
    }

    for (let i = 0; i < tournamentHomeState.selectedTournamentRegistrationFees.length; i++) {
      try {
        const postLineItemPromise: Promise<ICart> = cartActions.postLineItem(
          cart?.token,
          tournamentHomeState.selectedTournamentRegistrationFees[i]?.variant_id,
          tournamentHomeState.selectedTournamentRegistrationFees[i]?.quantity,
          false,
          tournamentHomeState.selectedTournamentRegistrationFees[i]?.id,
        ) as unknown as Promise<ICart>;

        cart = await postLineItemPromise;
      } catch {
        uiActions.dequeue();
        uiActions.showError(t("Failed to add line item to cart"));
        return;
      }
    }

    if (cart) {
      uiActions.dequeue();
      tournamentRegistrationActions.updateActiveRegistrationFees(
        tournamentHomeState.selectedTournamentRegistrationFees,
      );
      navigateToCheckout();
      return;
    }
  }

  function handleChangeQuantity(feeIndex: number, type: "add" | "subtract") {
    const selectedFees = tournamentHomeState.selectedTournamentRegistrationFees;

    if (type === "add") {
      selectedFees[feeIndex].quantity++;
    } else {
      selectedFees[feeIndex].quantity--;
    }

    setTournamentHomeState(prev => ({
      ...prev,
      selectedTournamentRegistrationFees: selectedFees,
    }));
  }

  function renderWarnings() {
    const tournamentWarning = determineTournamentRegistrationWarning(tournamentRegistrationStore.tournament);

    const localeStringOptions: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    };

    const openDateFormatString = new Date(
      tournamentRegistrationStore.tournament.registration_open_date + "Z",
    ).toLocaleString("en-CA", localeStringOptions);

    const closeDateFormatString = new Date(
      tournamentRegistrationStore.tournament.registration_close_date + "Z",
    ).toLocaleString("en-CA", localeStringOptions);

    switch (tournamentWarning) {
      case "closed":
        return (
          <Callout
            type="warning"
            title={t("Registration Closed")}
            content={t("Tournament is not open for registration")}
          />
        );
      case "will-open":
        return (
          <Callout
            type="warning"
            title={t("Registration is currently closed")}
            content={`${t("Registration for the tournament will open on")} ${openDateFormatString}`}
          />
        );
      case "closed-previously":
        return (
          <Callout
            type="warning"
            title={t("Registration is currently closed")}
            content={`${t("Registration for the tournament ended on")} ${closeDateFormatString}`}
          />
        );
      case "no-capacity":
        return (
          <Callout
            type="warning"
            title={t("Capacity Reached")}
            content={t("Tournament is currently at capacity")}
          />
        );
      case "none":
      default:
        return <></>;
    }
  }

  function checkDescriptionOverflow() {
    const element = document.getElementById("description_text");

    if (!element) {
      return;
    }

    setTournamentHomeState(prevState => ({
      ...prevState,
      descriptionOverflow: element.clientHeight < element.scrollHeight,
    }));
  }

  return (
    <Page>
      {tournamentRegistrationStore.facility && tournamentRegistrationStore.tournament && (
        <>
          <TournamentHeader
            facility={tournamentRegistrationStore.facility}
            tournament={tournamentRegistrationStore.tournament}
          />
          {renderWarnings()}
          <div className="tournament-home">
            <div className="tournament-home-registration-fees">
              {tournamentRegistrationStore.tournament?.description && (
                <Card title={t("Description")}>
                  <Card.Section>
                    <div
                      id="description_text"
                      className={
                        tournamentHomeState.fullDescriptionVisible
                          ? "tournament-register-description-full"
                          : "tournament-register-description"
                      }
                    >
                      <Markdown markdownText={tournamentRegistrationStore.tournament.description} />
                    </div>
                    {tournamentHomeState.descriptionOverflow && (
                      <div
                        className="league-register-description-read-more"
                        onClick={() =>
                          setTournamentHomeState(prevState => ({
                            ...prevState,
                            fullDescriptionVisible: !prevState.fullDescriptionVisible,
                          }))
                        }
                      >
                        {tournamentHomeState.fullDescriptionVisible
                          ? t("Show Less")
                          : t("Read More")}
                      </div>
                    )}
                  </Card.Section>
                </Card>
              )}
              {determineTournamentRegistrationWarning(tournamentRegistrationStore.tournament) === "none" && (
                <>
                <h1 className="tournament-home-registration-fee-header">
                  {t("Registration Fees")}
                </h1>
                {tournamentHomeState.tournamentRegistrationFees
                  ?.filter(fee => fee.type === "registration_fee")
                  ?.map(tournamentRegistrationFee => {
                    const id = tournamentRegistrationFee.id;
                    const isHovered = tournamentHomeState.hoveredTournamentRegistrationFeeId === id;
                    const isSelected =
                      tournamentHomeState.selectedTournamentRegistrationFees.findIndex(fee => fee.id === id) >= 0;
                    const isDisabled = tournamentRegistrationFee.players > tournamentSpotsLeft && !isSelected;

                    return (
                      <div
                        key={id}
                        className={classNames("tournament-home-registration-fee", {
                          "tournament-home-registration-fee-selected": isSelected,
                          "tournament-home-registration-fee-disabled": isDisabled,
                        })}
                        onMouseOver={() => hoverTournamentRegistrationFee(id, isDisabled)}
                        onMouseLeave={() => hoverTournamentRegistrationFee(undefined, isDisabled)}
                        onClick={() => selectTournamentRegistrationFee(tournamentRegistrationFee, isDisabled)}
                      >
                        <div
                          className={classNames("tournament-home-registration-fee-header", {
                            "tournament-home-registration-fee-header-selected": isSelected,
                          })}
                        >
                          <div>
                            <div>{tournamentRegistrationFee.product_title}</div>
                            {tournamentRegistrationFee.product_title !== tournamentRegistrationFee.variant_title && (
                              <div className="tournament-home-registration-fee-header-subtitle">
                                {tournamentRegistrationFee.variant_title}
                              </div>
                            )}
                          </div>

                          <FontAwesomeIcon
                            className={classNames({
                              "tournament-home-registration-fee-header-selection-default":
                                !isSelected && !isHovered && !isDisabled,
                              "tournament-home-registration-fee-header-selection-disabled": isDisabled,
                              "tournament-home-registration-fee-header-selection-hovered": !isSelected && isHovered,
                              "tournament-home-registration-fee-header-selection-selected": isSelected,
                            })}
                            icon={isSelected ? faCircleCheck : faCircle}
                          />
                        </div>
                        <hr
                          className={classNames("tournament-home-registration-fee-dividing-line", {
                            "tournament-home-registration-fee-dividing-line-highlighted": isHovered || isSelected,
                          })}
                        />
                        <div className="tournament-home-registration-fee-main">
                          <div className="flex justify-between">
                            <div>
                              <div className="tournament-home-registration-fee-main-price">
                                {formatTournamentRegistrationFeePrice(tournamentRegistrationFee)}
                              </div>
                              <div className="tournament-home-registration-fee-main-description">
                                This registration includes {tournamentRegistrationFee.players}{" "}
                                {tournamentRegistrationFee.players === 1 ? "player" : "players"}
                              </div>
                              <div className="tournament-home-registration-fee-main-description">
                                {tournamentRegistrationFee.description ?? ""}
                              </div>
                            </div>
                            {tournamentHomeState.selectedTournamentRegistrationFees.findIndex(
                              fee => fee.id === tournamentRegistrationFee.id,
                            ) >= 0 && (
                              <div className="flex justify-between" onClick={e => e.stopPropagation()}>
                                <div>
                                  <Button
                                    onClick={() =>
                                      handleChangeQuantity(
                                        tournamentHomeState.selectedTournamentRegistrationFees.findIndex(
                                          fee => fee.id === tournamentRegistrationFee.id,
                                        ),
                                        "subtract",
                                      )
                                    }
                                    type={"tertiary"}
                                    size="large"
                                    disabled={
                                      tournamentHomeState.selectedTournamentRegistrationFees.find(
                                        fee => fee.id === tournamentRegistrationFee.id,
                                      ).quantity <= 1
                                    }
                                  >
                                    <FontAwesomeIcon icon={faMinus} />
                                  </Button>
                                </div>
                                <div className="tournament-home-registration-fee-main-quantity">
                                  {
                                    tournamentHomeState.selectedTournamentRegistrationFees.find(
                                      fee => fee.id === tournamentRegistrationFee.id,
                                    ).quantity
                                  }
                                </div>
                                <div>
                                  <Button
                                    onClick={() =>
                                      handleChangeQuantity(
                                        tournamentHomeState.selectedTournamentRegistrationFees.findIndex(
                                          fee => fee.id === tournamentRegistrationFee.id,
                                        ),
                                        "add",
                                      )
                                    }
                                    disabled={tournamentRegistrationFee.players > tournamentSpotsLeft}
                                    type={"tertiary"}
                                    size="large"
                                  >
                                    <FontAwesomeIcon icon={faPlus} />
                                  </Button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}

                {tournamentHomeState.tournamentRegistrationFees?.filter(fee => fee.type === "add_on").length > 0 && (
                  <h1 className="tournament-home-registration-fee-header">{"Add Ons"}</h1>
                )}
                {tournamentHomeState.tournamentRegistrationFees
                  ?.filter(fee => fee.type === "add_on")
                  ?.map(tournamentRegistrationFee => {
                    const id = tournamentRegistrationFee.id;
                    const isHovered = tournamentHomeState.hoveredTournamentRegistrationFeeId === id;
                    const isSelected =
                      tournamentHomeState.selectedTournamentRegistrationFees.findIndex(fee => fee.id === id) >= 0;

                    return (
                      <div
                        key={id}
                        className={classNames("tournament-home-registration-fee", {
                          "tournament-home-registration-fee-selected": isSelected,
                        })}
                        onMouseOver={() => hoverTournamentRegistrationFee(id, false)}
                        onMouseLeave={() => hoverTournamentRegistrationFee(undefined, false)}
                        onClick={() => selectTournamentRegistrationFee(tournamentRegistrationFee, false)}
                      >
                        <div
                          className={classNames("tournament-home-registration-fee-header", {
                            "tournament-home-registration-fee-header-selected": isSelected,
                          })}
                        >
                          <div>
                            <div>{tournamentRegistrationFee.product_title}</div>
                            {tournamentRegistrationFee.product_title !== tournamentRegistrationFee.variant_title && (
                              <div className="tournament-home-registration-fee-header-subtitle">
                                {tournamentRegistrationFee.variant_title}
                              </div>
                            )}
                          </div>

                          <FontAwesomeIcon
                            className={classNames({
                              "tournament-home-registration-fee-header-selection-default": !isSelected && !isHovered,
                              "tournament-home-registration-fee-header-selection-hovered": !isSelected && isHovered,
                              "tournament-home-registration-fee-header-selection-selected": isSelected,
                            })}
                            icon={isSelected ? faCircleCheck : faCircle}
                          />
                        </div>
                        <hr
                          className={classNames("tournament-home-registration-fee-dividing-line", {
                            "tournament-home-registration-fee-dividing-line-highlighted": isHovered || isSelected,
                          })}
                        />
                        <div className="tournament-home-registration-fee-main">
                          <div className="flex justify-between">
                            <div>
                              <div className="tournament-home-registration-fee-main-price">
                                {formatTournamentRegistrationFeePrice(tournamentRegistrationFee)}
                              </div>
                              <div className="tournament-home-registration-fee-main-description">
                                {tournamentRegistrationFee.description ?? ""}
                              </div>
                            </div>
                            {tournamentHomeState.selectedTournamentRegistrationFees.findIndex(
                              fee => fee.id === tournamentRegistrationFee.id,
                            ) >= 0 && (
                              <div className="flex justify-between" onClick={e => e.stopPropagation()}>
                                <div>
                                  <Button
                                    onClick={() =>
                                      handleChangeQuantity(
                                        tournamentHomeState.selectedTournamentRegistrationFees.findIndex(
                                          fee => fee.id === tournamentRegistrationFee.id,
                                        ),
                                        "subtract",
                                      )
                                    }
                                    type={"tertiary"}
                                    size="large"
                                    disabled={
                                      tournamentHomeState.selectedTournamentRegistrationFees.find(
                                        fee => fee.id === tournamentRegistrationFee.id,
                                      ).quantity <= 1
                                    }
                                  >
                                    <FontAwesomeIcon icon={faMinus} />
                                  </Button>
                                </div>
                                <div className="tournament-home-registration-fee-main-quantity">
                                  {
                                    tournamentHomeState.selectedTournamentRegistrationFees.find(
                                      fee => fee.id === tournamentRegistrationFee.id,
                                    ).quantity
                                  }
                                </div>
                                <div>
                                  <Button
                                    onClick={() =>
                                      handleChangeQuantity(
                                        tournamentHomeState.selectedTournamentRegistrationFees.findIndex(
                                          fee => fee.id === tournamentRegistrationFee.id,
                                        ),
                                        "add",
                                      )
                                    }
                                    type={"tertiary"}
                                    size="large"
                                  >
                                    <FontAwesomeIcon icon={faPlus} />
                                  </Button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </>
              )}
              </div>
              {determineTournamentRegistrationWarning(tournamentRegistrationStore.tournament) === "none" && (
              <div className="tournament-home-order-summary">
                {windowSize.width <= TABLET_WIDTH && (
                  <div className="tournament-home-order-summary-callout-tablet">
                    {renderRegistrationAmountWarningCallout()}
                  </div>
                )}
                {tournamentHomeState.selectedTournamentRegistrationFees?.length > 0 ? (
                  <OrderSummary
                    headerBadge={{
                      type: "success",
                      children: (
                        <div>
                          {tournamentSpotsLeft}{" "}
                          {tournamentSpotsLeft === 1
                            ? t("Spot Left")
                            : t("Spots Left")}
                        </div>
                      ),
                    }}
                    subtotal={getTotalRegistrationFeePrice()}
                    taxLines={[]}
                    discount={0}
                    total={getTotalRegistrationFeePrice()}
                    displayLineItemsWithoutToggle={true}
                    lineItems={tournamentHomeState.selectedTournamentRegistrationFees.map(fee => {
                      return {
                        src: fee.product?.default_image?.source,
                        productTitle: fee.product_title,
                        variantTitle: fee.variant_title,
                        quantity: fee.quantity,
                        price: getTournamentRegistrationFeePrice(fee) * fee.quantity,
                      };
                    })}
                  >
                    <div className="tournament-home-order-summary-go-to-checkout-section">
                      <button
                        className="tournament-home-order-summary-go-to-checkout"
                        onClick={goToCheckout}
                        disabled={disabledGoToCheckout()}
                      >
                        {t("Checkout")}
                      </button>
                      {windowSize.width > TABLET_WIDTH && renderRegistrationAmountWarningCallout()}
                    </div>
                  </OrderSummary>
                ) : (
                  <OrderSummary
                    headerBadge={{
                      type: "success",
                      children: (
                        <div>
                          {tournamentSpotsLeft}{" "}
                          {tournamentSpotsLeft === 1
                            ? t("Spot Left")
                            : t("Spots Left")}
                        </div>
                      ),
                    }}
                    headerAction={{
                      label: t("Clear Cart"),
                      action: () =>
                        cartActions.cartClear({
                          tokenName: getTournamentCartTokenName(facilityShortName, tournamentHandle),
                        }),
                    }}
                    subtotal={cartStore?.cart?.subtotal_price ?? 0}
                    taxLines={cartStore?.cart?.tax_lines ?? []}
                    discount={cartStore?.cart?.total_discount ?? 0}
                    total={cartStore?.cart?.total_price ?? 0}
                    displayLineItemsWithoutToggle={true}
                    lineItems={cartStore?.cart?.line_items?.map(lineItem => {
                      return {
                        src: lineItem.product_default_image?.source,
                        productTitle: lineItem.product_title,
                        variantTitle: lineItem.variant_title,
                        quantity: lineItem.quantity,
                        price: lineItem.subtotal_price,
                      };
                    })}
                  >
                    <div className="tournament-home-order-summary-go-to-checkout-section">
                      <button
                        className="tournament-home-order-summary-go-to-checkout"
                        onClick={goToCheckout}
                        disabled={disabledGoToCheckout()}
                      >
                        {t("Checkout")}
                      </button>
                      {windowSize.width > TABLET_WIDTH && renderRegistrationAmountWarningCallout()}
                    </div>
                  </OrderSummary>
                )}
              </div>
              )}
            </div>
        </>
      )}
    </Page>
  );
}
